import { useEffect, useRef, useState } from "react";
import type { ProductData } from "@/mocks/products";

interface Props {
  product: ProductData;
}

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function ProductRitual({ product }: Props) {
  const { ref, inView } = useInView(0.1);

  return (
    <section className="relative bg-[#060508] py-40 px-6 overflow-hidden">
      {/* Divider line */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-b from-transparent"
        style={{ background: `linear-gradient(to bottom, transparent, ${product.accentColor}20)` }}
      />

      {/* Right glow */}
      <div
        className="absolute top-1/2 right-0 w-[400px] h-[400px] rounded-full pointer-events-none -translate-y-1/2"
        style={{
          background: `radial-gradient(circle, ${product.glowColor} 0%, transparent 70%)`,
          filter: "blur(80px)",
        }}
      />

      <div ref={ref} className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          {/* Text — left */}
          <div
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateX(0)" : "translateX(-30px)",
              transition: "opacity 1.8s ease, transform 1.8s ease",
            }}
          >
            <div
              className="text-xs tracking-[0.5em] uppercase mb-8"
              style={{ color: `${product.accentColor}50` }}
            >
              Ritual
            </div>
            <h2
              className="text-3xl md:text-4xl font-light text-white/80 mb-12 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.04em" }}
            >
              {product.ritualTitle}
            </h2>

            <div className="space-y-6">
              {product.ritualLines.map((line, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4"
                  style={{
                    opacity: inView ? 1 : 0,
                    transition: `opacity 2s ease ${0.4 + i * 0.25}s`,
                  }}
                >
                  <div
                    className="mt-2 w-4 h-px flex-shrink-0"
                    style={{ background: `${product.accentColor}40` }}
                  />
                  <p
                    className="text-white/35 text-lg font-light leading-relaxed"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {line}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Image — right */}
          <div
            className="relative overflow-hidden"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateX(0)" : "translateX(30px)",
              transition: "opacity 1.8s ease 0.3s, transform 1.8s ease 0.3s",
            }}
          >
            <div className="relative" style={{ paddingBottom: "75%" }}>
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${product.ritualImage})`,
                  filter: "brightness(0.5) saturate(0.65)",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-l from-[#060508]/60 via-transparent to-transparent" />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  boxShadow: `inset 0 0 60px ${product.glowColor}`,
                  border: `1px solid ${product.accentColor}15`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
